import { CurrencyPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as ibantools from 'ibantools';
import { Observable, Subject, of } from 'rxjs';
import { filter, finalize, map, share, take, takeUntil, tap } from 'rxjs/operators';

import { ClientBankAccount } from '../../api/models/client-bank-account';
import { FieldError } from '../../api/models/field-error';
import { GenerateOfferRequestBody } from '../../api/models/generate-offer-request-body';
import { Invoice } from '../../api/models/invoice';
import { InvoiceNotReadyError } from '../../api/models/invoice-not-ready-error';
import { InvoiceNotReadyViolation } from '../../api/models/invoice-not-ready-violation';
import { Money } from '../../api/models/money';
import { Permission } from '../../api/models/permission';
import { ReplaceInvoiceInformationRequest } from '../../api/models/replace-invoice-information-request';
import { SendInvoiceRequestBody } from '../../api/models/send-invoice-request-body';
import { SendSubfinancingInvoiceRequestBody } from '../../api/models/send-subfinancing-invoice-request-body';
import { InvoicesService } from '../../api/services/invoices.service';
import {
  AdvanceInvoiceNumberDialogComponent,
  AdvanceInvoiceNumberDialogData,
} from '../../components/advance-invoice-number-dialog/advance-invoice-number-dialog.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogDetail,
  ConfirmationDialogModel,
} from '../../components/confirmation-dialog/confirmation-dialog.component';
import { ClientRepresentative } from '../../model/client-representative';
import { LeadSource } from '../../model/lead-source';
import { ClientRepresentativesResource } from '../../model/model-resource';
import { Partner } from '../../model/partner';
import { ClientPeerService } from '../../service/client-peer.service';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { UserService } from '../../service/user.service';
import { ConfirmationService } from '../../shared/confirmation/confirmation.service';
import { DateFields, clearFieldErrorOnEdit, toIsoDate } from '../../shared/date-fields/date-field.util';
import { financingHoldsTooltip } from '../../shared/financing-holds/financing-hold-text';
import { toElectronicIban } from '../../shared/iban/iban.util';
import { INFO_EDIT_POLICY, INFO_FIELDS, InfoField } from '../../shared/info-edit-policy.gen';
import { ValidateEmailList } from '../../validators/email-list.validator';

/**
 * EditablePolicy is the server's per-state locked-fields guard
 * (`invoicestate.InfoEditPolicyFor`) for one state, read from its generated
 * copy (`INFO_EDIT_POLICY`). Each field entry reports whether the
 * corresponding `ReplaceInvoiceInformationRequest` field is editable in
 * the current state. The widget consults this map to gate
 * `mat-form-field` rendering in edit mode; the server is still the
 * source of truth and rejects mismatches with `invoice_field_locked`.
 */
type EditablePolicy = { readonly allowed: boolean } & Readonly<Record<InfoField, boolean>>;

/** The Information fields that are date pickers. */
type InformationDateField = 'date' | 'due_date';

/**
 * SubfinancingOverride pairs one overridable send-subfinancing violation with
 * the request flag that clears it and the server's message for it. The
 * send-subfinancing 422 can carry both overridable violations at once, so the
 * recovery walks a list of these rather than branching on a single code.
 */
interface SubfinancingOverride {
  flag: 'allow_in_unexpected_state' | 'allow_resend';
  message: string;
}

// Why the "Original Invoice Date" field refuses a day past its picker's
// limit. The two limits use the server's words (invoice.validateInfoDates),
// so a day the picker refuses reads the same as one the server refuses.
const DATE_AFTER_DUE_DATE = 'must not be after the due date';
const DATE_IN_FUTURE = 'must not be in the future';

// Why "Bank Account" refuses a changed IBAN, in the server's words
// (invoice_information_invalid on `iban`), so the browser's refusal and the
// server's read the same.
const IBAN_INVALID = 'Not a valid IBAN';

// Why a save is refused when the invoice's stored IBAN is one the server
// cannot read: invoice_persisted_iban_invalid, or an
// invoice_information_invalid field error on `iban` while Bank Account is
// read-only. The handler cannot fix it in this form, so it says whom to ask.
const PERSISTED_IBAN_INVALID = 'The stored IBAN is not valid. Ask support to correct it.';

// What the banner says for a refusal the widget does not know when the
// server's message is blank.
const SAVING_FAILED = 'Saving failed';

// The label each field of the Information body goes by on screen. A server
// field error for a field that is not on screen goes into the error banner
// under this label, e.g. "Due Date: field locked in current state".
const FIELD_LABELS: Readonly<Record<keyof ReplaceInvoiceInformationRequest, string>> = {
  amount: 'Amount',
  iban: 'Bank Account',
  date: 'Original Invoice Date',
  due_date: 'Due Date',
  express_payment: 'Express Payment',
  new_invoice_number: 'Corona Invoice Number',
  new_reference_number: 'Corona Reference Number',
  number: 'Original Invoice Number',
  original_reference_number: 'Original Reference Number',
  lead_source_id: 'Lead Source',
  responsible_seller_id: 'Responsible Seller',
};

function isInformationField(field: string): field is keyof ReplaceInvoiceInformationRequest {
  return Object.prototype.hasOwnProperty.call(FIELD_LABELS, field);
}

// The policy with `allowed` and each Information field's entry set by
// `editable`.
function editablePolicyOf(allowed: boolean, editable: (field: InfoField) => boolean): EditablePolicy {
  const fields = {} as Record<InfoField, boolean>;
  for (const field of INFO_FIELDS) {
    fields[field] = editable(field);
  }
  return { ...fields, allowed };
}

const POLICY_NONE = editablePolicyOf(false, () => false);

@Component({
  selector: 'app-invoice-info-v2-widget',
  templateUrl: './invoice-info-v2-widget.component.html',
  styleUrls: ['./invoice-info-v2-widget.component.scss'],
  providers: [CurrencyPipe],
})
export class InvoiceInfoV2WidgetComponent implements OnInit, OnDestroy {
  private readonly invoicePeerService = inject(InvoicePeerService);
  private readonly invoicesService = inject(InvoicesService);
  private readonly clientPeerService = inject(ClientPeerService);
  private readonly builder = inject(UntypedFormBuilder);
  // Transitional injections for the carried-over Action menu. Removed
  // when issue #247 relocates the menu off the Information widget.
  private readonly clientRepresentativeResource = inject(ClientRepresentativesResource);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly userService = inject(UserService);

  public readonly placeholder = '—';

  public invoice$: Observable<Invoice>;
  public bankAccounts$: Observable<ClientBankAccount[]>;
  // Gates the "Send Invoice (new)" menu entry on the permission the backend
  // enforces on the send endpoints, whichever role granted it.
  public canSendInvoice$: Observable<boolean>;
  // Gates the "Send to Subfinancing" menu entry. The action needs BOTH the
  // permission and a subfinancing provider on the invoice, so the template
  // additionally checks `invoice.subfinancing` — which is also where the
  // provider's name in the label comes from.
  public canSendSubfinancing$: Observable<boolean>;
  // Gates the two document-generation menu entries on the permission the
  // backend now enforces on those endpoints. The server-side check is the
  // real boundary; hiding the items is the usability half.
  public canGenerateDocs$: Observable<boolean>;
  // Gates the Edit button on the permission the backend enforces on
  // PUT /information. Unlike the Action menu entries, Edit stays visible
  // without it: the button is disabled, with a tooltip saying why.
  public canEditInformation$: Observable<boolean>;
  // The Edit wrapper's tooltip; the template binds it only while the
  // permission is missing.
  public readonly editPermissionTooltip = "You don't have permission to edit invoice information";

  public invoice: Invoice | null = null;
  public form: UntypedFormGroup;
  public offerForm: UntypedFormGroup;
  public finalReportForm: UntypedFormGroup;

  public edited = false;
  public locked = false;
  public saving = false;
  public offerEdited = false;
  public finalReportEdited = false;

  public selectingLeadSource = false;
  public selectingResponsibleSeller = false;
  public selectedLeadSource: LeadSource | null = null;
  public selectedResponsibleSeller: Partner | null = null;

  public stateLockedMessage: string | null = null;
  public reloadMessage: string | null = null;
  public fieldErrors: Record<string, string> = {};
  // Server field errors for fields not on screen, as "<label>: <message>".
  public bannerFieldErrors: string[] = [];

  public readonly ibanInvalidMessage = IBAN_INVALID;

  // Original Invoice Date and Due Date: each picker's earliest day and the
  // field's one message. Original Invoice Date also has an upper limit,
  // `latestInvoiceDate`, named in the server's words.
  public readonly dateFields: DateFields<InformationDateField>;

  // Said beside Express Payment when the client asked for it at offer
  // approval (`client_requested_express_payment`) and express payment is
  // off: approval leaves it off for a bank that can't receive one, and a
  // handler can turn it off later. The invoice doesn't record which, so the
  // line says only what the client asked for.
  public readonly clientExpressRequestNote = 'The client asked for express payment';

  // Said under Original Reference Number while it is marked required.
  public readonly originalReferenceRequiredNote = 'Required: the client has Original reference payments on';

  public readonly expressPaymentChoices = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  private editEtag: string | null = null;
  // The IBAN the form opened with, in electronic format: the one the handler
  // has not touched, which the browser's check lets through, valid or not.
  private editIban: string | null = null;
  private readonly ngDestroy$ = new Subject<void>();

  constructor() {
    this.form = this.builder.group({
      amount: [null],
      iban: [null, (control: AbstractControl) => this.validateChangedIban(control)],
      date: [null],
      due_date: [null],
      express_payment: [null],
      new_invoice_number: [null],
      new_reference_number: [null],
      number: [null],
      original_reference_number: [null],
      lead_source_id: [null],
      responsible_seller_id: [null],
    });
    this.dateFields = new DateFields<InformationDateField>(this.form, (field) => this.invoice?.[field], {
      date: (typed) => this.dateLimitMessage(typed),
    });
    this.offerForm = this.builder.group({
      recipients: [null, ValidateEmailList],
    });
    this.finalReportForm = this.builder.group({
      recipients: [null, ValidateEmailList],
    });
  }

  /**
   * Returns the editable-fields policy for the given invoice state: the
   * server's `invoicestate.InfoEditPolicyFor`, from its generated copy
   * `INFO_EDIT_POLICY` (`just regen-openapi` writes it from the Go source).
   * A field is editable when the state allows an Information edit and does
   * not lock the field. The server still validates and the policy here is
   * purely a UX gate for which inputs to render.
   */
  public editablePolicy(invoice: Invoice | null): EditablePolicy {
    const id = invoice?.state?.id;
    const server = id ? INFO_EDIT_POLICY[id] : undefined;
    if (!server?.allowed) {
      return POLICY_NONE;
    }
    return editablePolicyOf(true, (field) => !server.locked_fields.includes(field));
  }

  /**
   * Whether the editor marks Original Reference Number required: the
   * invoice's client has Original reference payments on, so its financing
   * payout and final payment need the reference. Reads the effective value
   * the server reports in the client's `preferences`, never the client type;
   * a client without them reads as off. The mark adds no validator.
   */
  public originalReferenceRequired(invoice: Invoice): boolean {
    return invoice.client?.preferences?.original_reference_payments?.value === true;
  }

  /**
   * Whether Express Payment carries `clientExpressRequestNote`: the client
   * asked for express payment and the invoice's express payment is off.
   */
  public showsClientExpressRequest(invoice: Invoice): boolean {
    return invoice.client_requested_express_payment === true && !invoice.express_payment;
  }

  public ngOnInit(): void {
    this.invoice$ = this.invoicePeerService.getInvoice().pipe(
      tap((invoice) => {
        this.invoice = invoice;
      }),
    );

    this.canSendInvoice$ = this.userService.hasPermission(Permission.InvoiceSend);
    this.canSendSubfinancing$ = this.userService.hasPermission(
      Permission.InvoiceSendSubfinancing,
    );
    this.canGenerateDocs$ = this.userService.hasPermission(Permission.InvoiceDocsGenerate);
    this.canEditInformation$ = this.userService.hasPermission(Permission.InvoiceEditInformation);

    this.bankAccounts$ = this.clientPeerService.get().pipe(
      takeUntil(this.ngDestroy$),
      map((client) => client.bank_accounts ?? []),
      share(),
    );

    this.invoicePeerService
      .getState()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((state) => {
        this.locked = state === 'locked';
      });

    // The server's refusal of an IBAN, a date or an original reference
    // number gives way to the browser's own check once the handler edits
    // that field.
    clearFieldErrorOnEdit(
      this.form,
      ['iban', 'date', 'due_date', 'original_reference_number'],
      this,
      this.ngDestroy$,
    );
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public edit(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicePeerService.lock();
    this.edited = true;
    this.editEtag = this.invoice.etag ?? null;
    this.editIban = toElectronicIban(this.invoice.iban);
    this.clearErrors();
    this.selectedLeadSource = this.invoice.lead_source
      ? this.toLegacyLeadSource(this.invoice.lead_source)
      : null;
    this.selectedResponsibleSeller = this.invoice.responsible_seller
      ? this.toLegacyPartner(this.invoice.responsible_seller)
      : null;
    this.form.reset({
      amount: this.invoice.amount ? Number(this.invoice.amount.amount) : null,
      iban: this.invoice.iban ?? null,
      date: this.invoice.date ? new Date(this.invoice.date) : null,
      due_date: this.invoice.due_date ? new Date(this.invoice.due_date) : null,
      express_payment: this.invoice.express_payment ?? false,
      new_invoice_number: this.invoice.new_number ?? null,
      new_reference_number: this.invoice.new_reference_number ?? null,
      number: this.invoice.number ?? null,
      original_reference_number: this.invoice.original_reference_number ?? null,
      lead_source_id: this.invoice.lead_source_id ?? null,
      responsible_seller_id: this.invoice.responsible_seller_id ?? null,
    });
    this.form.markAsPristine();
  }

  public revert(): void {
    this.invoicePeerService.unlock();
    this.edited = false;
    this.editEtag = null;
    this.selectingLeadSource = false;
    this.selectingResponsibleSeller = false;
    this.selectedLeadSource = null;
    this.selectedResponsibleSeller = null;
    this.form.reset();
    this.clearErrors();
  }

  public save(): void {
    if (!this.invoice || !this.editEtag) {
      return;
    }
    this.clearErrors();

    const body = this.buildPutBody(this.invoice);

    this.saving = true;

    this.invoicesService
      .replaceInvoiceInformation({
        id: this.invoice.id,
        'If-Match': this.editEtag,
        body,
      })
      // The HttpErrorInterceptor answers a 403 with its own dialog and ends
      // the request with neither a value nor an error, so Save is re-enabled
      // however the request ends. The form stays in edit mode; Cancel leaves.
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (updated) => {
          this.edited = false;
          this.editEtag = null;
          this.invoicePeerService.unlock();
          this.invoicePeerService.invoiceChanged(updated);
        },
        error: (err: HttpErrorResponse) => this.handleError(err),
      });
  }

  public reload(): void {
    if (!this.invoice) {
      return;
    }
    this.reloadMessage = null;
    this.invoicePeerService.initialize(this.invoice.id);
  }

  /**
   * The latest day the "Original Invoice Date" picker offers: the earlier of
   * today and the form's due date. `[max]` is also a validator, so a typed
   * day past it keeps the form from saving, and `dateFields.message` says
   * why, in the server's words.
   * The server still decides, by the Helsinki day, for every value the form
   * lets through. The ISO string suits both the app's MomentDateAdapter and
   * the native one.
   *
   * The stored date always stays within it. A stored date that intake left
   * after the due date or in the future would otherwise invalidate the form
   * and block an unrelated save, such as an IBAN fix, which the server
   * deliberately accepts.
   */
  public latestInvoiceDate(): string {
    const today = this.today();
    const dueDate = this.formDueDate();
    const latest = dueDate !== '' && dueDate < today ? dueDate : today;
    const stored = this.invoice?.date;
    return stored && stored > latest ? stored : latest;
  }

  // The limit is the earlier of today and the due date, or the stored date
  // when that is later, so a refused day is past today, the due date, or
  // both, even when the stored date lifts the limit. Past one of them, that
  // one is named; otherwise the earlier one, which binds.
  private dateLimitMessage(typed: string | null): string {
    const today = this.today();
    const dueDate = this.formDueDate();
    const afterDueDate = typed !== null && dueDate !== '' && typed > dueDate;
    const inFuture = typed !== null && typed > today;
    if (afterDueDate !== inFuture) {
      return afterDueDate ? DATE_AFTER_DUE_DATE : DATE_IN_FUTURE;
    }
    return dueDate !== '' && dueDate <= today ? DATE_AFTER_DUE_DATE : DATE_IN_FUTURE;
  }

  private today(): string {
    return toIsoDate(new Date()) as string;
  }

  // The due date the form holds, falling back to the stored one; '' if none.
  private formDueDate(): string {
    return this.toIsoDateOrFallback(this.form.controls['due_date'].value, this.invoice?.due_date);
  }

  public hasFieldError(field: string): boolean {
    return field in this.fieldErrors;
  }

  public getFieldError(field: string): string | null {
    return this.fieldErrors[field] ?? null;
  }

  public selectLeadSource(): void {
    this.selectingLeadSource = true;
  }

  public leadSourceSelected(leadSource?: LeadSource): void {
    this.selectingLeadSource = false;
    this.selectedLeadSource = leadSource ?? null;
    this.form.patchValue({
      lead_source_id: leadSource?.leadSourceId ?? null,
    });
  }

  public leadSourceCleared(): void {
    this.selectingLeadSource = false;
    this.selectedLeadSource = null;
    this.form.patchValue({ lead_source_id: null });
  }

  public selectResponsibleSeller(): void {
    this.selectingResponsibleSeller = true;
  }

  public responsibleSellerSelected(partner?: Partner): void {
    this.selectingResponsibleSeller = false;
    this.selectedResponsibleSeller = partner ?? null;
    this.form.patchValue({
      responsible_seller_id: partner?.partnerId ?? null,
    });
  }

  public responsibleSellerCleared(): void {
    this.selectingResponsibleSeller = false;
    this.selectedResponsibleSeller = null;
    this.form.patchValue({ responsible_seller_id: null });
  }

  // ──────────────────────────────────────────────────────────────────
  // Transitional Action menu — see design.md "Transitional Action
  // menu" and issue #247. The
  // remaining legacy paths route through InvoicePeerService (which wraps
  // the /apiv2 InvoicesResource); the modern paths call InvoicesService
  // directly. Email pre-fill for the sub-forms reads
  // client_representative_id off the modern Invoice.
  // ──────────────────────────────────────────────────────────────────

  public generateFinalReport(): void {
    if (!this.invoice) {
      return;
    }
    this.getClientRepresentativeEmail(this.invoice)
      .pipe(take(1))
      .subscribe((email) => {
        this.invoicePeerService.lock();
        this.finalReportEdited = true;
        this.finalReportForm.patchValue({ recipients: email });
      });
  }

  public cancelFinalReport(): void {
    this.invoicePeerService.unlock();
    this.finalReportEdited = false;
  }

  public saveFinalReport(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicePeerService.unlock();
    this.finalReportEdited = false;
    this.invoicePeerService.generateFinalReport(
      this.invoice.id,
      this.finalReportForm.value.recipients,
    );
  }

  // Generate Invoice Number routes through the modern
  // /api/invoices/{id}/generate-invoice-number endpoint, which mints the
  // number and its payment reference only when the invoice has none —
  // unlike the legacy /apiv2 action, where a second click silently bumped
  // the ordinal (that bump is now the explicit Advance Invoice Number).
  //
  // The $Response variant is used because the status code carries the whole
  // signal (design D2): 201 minted, 200 the invoice already had a valid
  // number. Both bodies are the updated Invoice, pushed into the page so it
  // updates before the realtime invoice_updated event arrives.
  public generateInvoiceNumber(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicesService.generateInvoiceNumber$Response({ id: this.invoice.id }).subscribe({
      next: (resp) => {
        this.invoicePeerService.invoiceChanged(resp.body);
        if (resp.status === 200) {
          this.snackBar.open(
            `Invoice number ${resp.body.new_number} was already generated.`,
            'Dismiss',
            { duration: 5000 },
          );
        }
      },
      error: (err: HttpErrorResponse) => this.handleGenerateInvoiceNumberError(err),
    });
  }

  // Every refusal is a 422 carrying error_code plus a message that names the
  // fix (design D7), and the HttpErrorInterceptor re-throws a coded body
  // without opening its dialog — so this widget is the only place those
  // messages can reach the operator. Anything else falls back to a generic
  // snackbar so the click is never silent.
  private handleGenerateInvoiceNumberError(err: HttpErrorResponse): void {
    if (err.status === 422 && this.isCodedError(err.error)) {
      this.snackBar.open(
        err.error.message,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Failed to generate invoice number. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // Advance Invoice Number is the deliberate form of the ordinal bump the
  // legacy action performed on every repeat click. It is confirmed upfront
  // because it is not undoable in the operator's terms: every document
  // already generated carries the old number, so the invoice has to be
  // regenerated and re-sent afterwards. The entry is hidden for an invoice
  // with no number at all — there is nothing to advance, generate first.
  //
  // The confirmation is a dedicated dialog rather than the shared
  // ConfirmationService because the advance also demands a reason, which is
  // recorded as a priority comment on the invoice (design D8/D9). The dialog
  // closes with the trimmed reason on confirm and with undefined on cancel,
  // escape or a backdrop click, so a string coming back *is* the consent.
  // This side trims too and decides on the trimmed value, so the consent the
  // widget acts on is exactly the reason it sends and a blank-after-trim
  // result reads as a cancel rather than as a request the server would
  // refuse with reason_required.
  // The next number shown is computed here purely for display — the server
  // mints the authoritative one and refuses the call outright when the
  // ordinal is exhausted, the stored number was hand-edited, or the invoice
  // has reached accounting.
  public advanceInvoiceNumber(): void {
    const invoice = this.invoice;
    if (!invoice?.new_number) {
      return;
    }
    const current = invoice.new_number;
    this.dialog
      .open<AdvanceInvoiceNumberDialogComponent, AdvanceInvoiceNumberDialogData, string>(
        AdvanceInvoiceNumberDialogComponent,
        {
          maxWidth: '480px',
          data: { currentNumber: current, nextNumber: current + 1 },
        },
      )
      .afterClosed()
      .pipe(
        map((reason) => (typeof reason === 'string' ? reason.trim() : '')),
        filter((reason) => reason !== ''),
      )
      .subscribe((reason: string) => this.postAdvanceInvoiceNumber(invoice.id, reason));
  }

  private postAdvanceInvoiceNumber(id: number, reason: string): void {
    this.invoicesService.advanceInvoiceNumber({ id, body: { reason } }).subscribe({
      next: (updated: Invoice) => {
        this.invoicePeerService.invoiceChanged(updated);
        this.snackBar.open(
          `Invoice number advanced to ${updated.new_number}. The reason was recorded as a comment on the invoice.`,
          'Dismiss',
          { duration: 5000 },
        );
      },
      error: (err: HttpErrorResponse) => this.handleAdvanceInvoiceNumberError(err),
    });
  }

  // The 422 refusals and the 409 both carry error_code plus the server's
  // message, so the HttpErrorInterceptor re-throws them without opening its
  // own dialog and this snackbar is the single thing the operator sees. The
  // 409 is the lost race rather than a refusal — someone else advanced the
  // number between the read and the write — so it additionally refreshes
  // the page, and the operator decides again against the number that
  // actually exists now.
  private handleAdvanceInvoiceNumberError(err: HttpErrorResponse): void {
    if (err.status === 409) {
      this.invoicePeerService.refresh();
    }
    if ((err.status === 422 || err.status === 409) && this.isCodedError(err.error)) {
      this.snackBar.open(
        err.error.message,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Failed to advance invoice number. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // isCodedError recognises the shared Error body (`error_code` + `message`)
  // the invoice-number endpoints refuse with, and which the
  // HttpErrorInterceptor leaves to the widget without its dialog (so the
  // Information save's fallback uses it too). Unlike isNetvisorRejected it
  // does not pin a single code: these endpoints have one failing
  // precondition at a time and the server's message is what gets rendered,
  // so the widget does not need to branch per code.
  private isCodedError(body: unknown): body is { error_code: string; message: string } {
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const candidate = body as { error_code?: unknown; message?: unknown };
    return typeof candidate.error_code === 'string' && typeof candidate.message === 'string';
  }

  // Generate Offer routes through cctool-go's
  // /api/invoices/{id}/generate-offer endpoint, whose state advance is
  // guarded, never through the legacy app's generateOffer, which advanced
  // the invoice from whatever state it was in.
  public generateOffer(): void {
    if (!this.invoice) {
      return;
    }
    this.getClientRepresentativeEmail(this.invoice)
      .pipe(take(1))
      .subscribe((email) => {
        this.invoicePeerService.lock();
        this.offerEdited = true;
        this.offerForm.patchValue({ recipients: email });
      });
  }

  public cancelOffer(): void {
    this.invoicePeerService.unlock();
    this.offerEdited = false;
  }

  public saveOffer(): void {
    if (!this.invoice) {
      return;
    }
    const recipientsRaw = (this.offerForm.value.recipients ?? '') as string;
    const recipients = recipientsRaw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const invoice = this.invoice;
    const id = invoice.id;

    // Upfront confirmation: emailing the offer to the client and advancing
    // the invoice is non-reversible, so confirm intent before the first POST.
    // The sub-form stays open on cancel so the recipients can be adjusted.
    // The 422 "Send offer anyway?" recovery dialog re-enters via
    // postGenerateOffer and is NOT re-prompted here.
    this.confirmationService
      .confirm(this.buildSendOfferConfirmation(invoice, recipients))
      .pipe(filter((confirmed) => confirmed))
      .subscribe(() => {
        this.closeOfferForm();
        this.postGenerateOffer(id, { recipients });
      });
  }

  private closeOfferForm(): void {
    this.invoicePeerService.unlock();
    this.offerEdited = false;
  }

  // buildSendOfferConfirmation composes the send-offer confirmation. Per the
  // WHO → WHAT convention: the email recipients first, then the amount. When
  // recipients is empty the backend builds the bundle without emailing, which
  // the row makes explicit.
  //
  // Amount here is `amount_financed` ON PURPOSE — the offer IS the financing
  // offer to the client, so the advance we propose to pay is the figure that
  // characterises it. This differs from the send-invoice confirm, which shows
  // the invoice total billed to the end customer (`invoice.amount`).
  private buildSendOfferConfirmation(
    invoice: Invoice,
    recipients: string[],
  ): ConfirmationDialogModel {
    const details: ConfirmationDialogDetail[] = [
      // WHO
      {
        label: 'Recipients:',
        value: recipients.length > 0 ? recipients.join('\n') : '(no email — offer bundle only)',
      },
      // WHAT — the financed advance the offer proposes (see note above).
      { label: 'Amount:', value: this.formatMoney(invoice.amount_financed) },
    ];
    return new ConfirmationDialogModel(
      'Send offer to client?',
      "This emails the offer to the client and advances the invoice. It cannot be unsent without the client's cooperation.",
      'Send offer',
      'Cancel',
      details,
    );
  }

  // postGenerateOffer wraps the modern `POST /invoices/{id}/generate-offer`
  // call so the 422 → confirm-and-retry path for
  // `invoice_state_not_waiting_for_offer` can be retried with the same
  // body shape from one place. Other readiness violations and generic
  // failures continue through the snackbar path.
  private postGenerateOffer(id: number, body: GenerateOfferRequestBody): void {
    this.invoicesService.generateOffer({ id, body }).subscribe({
      error: (err: HttpErrorResponse) => this.handleGenerateOfferError(err, id, body),
    });
  }

  // Generate Subfinancing Invoice routes through
  // /api/invoices/{id}/generate-subfinancing-invoice (Subfinancing
  // mode): content-identical to the real invoice but stored under the
  // distinct Subfinancing Invoice attachment types and dated today when
  // the finance date is missing. Also 202, no recipients, fire-and-forget.
  public generateSubfinancingInvoice(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicesService.generateSubfinancingInvoice({ id: this.invoice.id }).subscribe({
      next: () => this.notifyInvoiceGenerationStarted(),
      error: (err: HttpErrorResponse) => this.handleGenerateInvoiceError(err),
    });
  }

  private notifyInvoiceGenerationStarted(): void {
    this.snackBar.open(
      'Invoice generation started. The document will appear in Attachments once ready.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // handleGenerateInvoiceError surfaces the full readiness violation set
  // for a 422 `invoice_not_ready_for_invoice_generation` in the
  // multi-line snackbar — the operator must resolve every listed
  // precondition (missing configuration, unresolvable invoice view,
  // missing due date, incomplete end-customer invoicing fields, or — in
  // `Real` mode — a missing finance date) before generation can run.
  // Unlike the offer flow there is no override path: invoice generation
  // has no state-only escape hatch. Non-422 errors fall through to a
  // generic snackbar so the click is never silent.
  private handleGenerateInvoiceError(err: HttpErrorResponse): void {
    if (err.status === 422 && this.isInvoiceNotReadyForGenerationError(err.error)) {
      const lines = err.error.violations.map(
        (v: InvoiceNotReadyViolation) => `• ${v.message}`,
      );
      this.snackBar.open(
        `Cannot generate invoice:\n${lines.join('\n')}`,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Could not generate invoice. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  private isInvoiceNotReadyForGenerationError(body: unknown): body is InvoiceNotReadyError {
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const candidate = body as Partial<InvoiceNotReadyError>;
    return (
      candidate.error_code === 'invoice_not_ready_for_invoice_generation' &&
      Array.isArray(candidate.violations)
    );
  }

  public sendInvoice(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicePeerService.sendInvoice(this.invoice.id);
  }

  // The "(new)" Send Invoice routes through the modern
  // /api/invoices/{id}/send endpoint: SendInvoiceWorkflow builds the
  // Apix package and transmits the invoice as a real verkkolasku. The
  // mode mirrors the legacy options — 'einvoice' merges the watermarked
  // original and routes to the end customer's eInvoice address, 'paper'
  // sends the Corona invoice only and forces print delivery. Kept as a
  // separate menu entry alongside the legacy send until the Apix
  // pipeline has earned production trust.
  public sendInvoiceNew(mode: SendInvoiceRequestBody['mode']): void {
    if (!this.invoice) {
      return;
    }
    // Upfront confirmation: sending the invoice to the end customer is
    // non-reversible (we cannot recall it without the recipient), so confirm
    // intent against the real recipient/amount before the first POST. A
    // subsequent 422-recovery retry (allow_in_unexpected_state / allow_resend)
    // re-enters via postSendInvoice and is NOT re-prompted here.
    const invoice = this.invoice;
    this.confirmationService
      .confirm(this.buildSendInvoiceConfirmation(invoice, mode))
      .pipe(filter((confirmed) => confirmed))
      .subscribe(() => this.postSendInvoice(invoice.id, { mode }));
  }

  // buildSendInvoiceConfirmation composes the send-invoice confirmation from
  // frontend-held invoice data — the same stored end-customer routing the
  // backend uses for the send, so the dialog and the actual send agree.
  // Detail order follows WHO → WHAT → WHEN: recipient first (end-customer
  // name + business id, plus the e-invoice operator/address for 'einvoice'
  // mode), then the amount, then the due date. Paper mode and a missing
  // e-invoice route omit the route rows gracefully — the backend still
  // enforces routing server-side, so correctness does not depend on the dialog.
  //
  // Amount is `invoice.amount` (the original invoice total the end customer is
  // billed) — NOT `amount_financed` (our advance to the client, a fraction of
  // the total). The confirm exists so the operator reads the real figure being
  // transmitted; showing the financed advance would understate it. This matches
  // the widget's own Amount field (`data.amount`) and edit form.
  private buildSendInvoiceConfirmation(
    invoice: Invoice,
    mode: SendInvoiceRequestBody['mode'],
  ): ConfirmationDialogModel {
    const endCustomer = invoice.end_customer;
    const route = endCustomer?.einvoice_address;

    const details: ConfirmationDialogDetail[] = [
      // WHO
      { label: 'Recipient:', value: endCustomer?.name || this.placeholder },
      { label: 'Business ID:', value: endCustomer?.business_id || this.placeholder },
    ];
    if (mode === 'einvoice' && route) {
      details.push(
        { label: 'E-invoice operator:', value: route.operator || this.placeholder },
        { label: 'E-invoice address:', value: route.address || this.placeholder },
      );
    }
    // WHAT — the invoice total billed to the end customer (see note above).
    details.push({ label: 'Amount:', value: this.formatMoney(invoice.amount) });
    // WHEN
    details.push({ label: 'Due date:', value: invoice.due_date || this.placeholder });

    const channel = mode === 'einvoice' ? 'e-invoice' : 'paper';
    return new ConfirmationDialogModel(
      'Send invoice to end customer?',
      `This transmits the invoice to the end customer by ${channel}. Once sent it cannot be recalled without the recipient's cooperation.`,
      'Send invoice',
      'Cancel',
      details,
    );
  }

  // formatMoney renders a Money for a confirmation detail row through the same
  // CurrencyPipe the page uses (LOCALE_ID 'fi'), so the confirmation amount
  // reads identically to the widget's own Amount field rather than as a raw
  // "<amount> <currency>" string.
  private formatMoney(money?: Money): string {
    if (!money?.amount) {
      return this.placeholder;
    }
    return this.currencyPipe.transform(money.amount, money.currency) ?? this.placeholder;
  }

  // postSendInvoice wraps the modern `POST /invoices/{id}/send` call so
  // the 422 → confirm-and-retry path for
  // `invoice_state_not_waiting_for_invoice_to_end_customer` can re-POST
  // the same body shape from one place.
  private postSendInvoice(id: number, body: SendInvoiceRequestBody): void {
    this.invoicesService.sendInvoice({ id, body }).subscribe({
      next: () => this.notifyInvoiceSendStarted(),
      error: (err: HttpErrorResponse) => this.handleSendInvoiceError(err, id, body),
    });
  }

  private notifyInvoiceSendStarted(): void {
    this.snackBar.open(
      'Invoice send started. The sent package will appear in Attachments once transmitted.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // handleSendInvoiceError mirrors handleGenerateOfferError: a 422 whose
  // only violation is an overridable precondition (unexpected state, or an
  // already-sent invoice) opens a confirmation dialog and re-POSTs with the
  // matching override on confirm; any other violation set (including mixed
  // overridable ones) surfaces in the multi-line snackbar. Non-422 errors
  // fall through to a generic snackbar so the click is never silent.
  private handleSendInvoiceError(
    err: HttpErrorResponse,
    id: number,
    body: SendInvoiceRequestBody,
  ): void {
    if (err.status === 422 && this.isInvoiceNotReadyForSendError(err.error)) {
      const violations = err.error.violations;
      if (violations.length === 1) {
        const only = violations[0];
        if (
          only.code === 'invoice_state_not_waiting_for_invoice_to_end_customer' &&
          !body.allow_in_unexpected_state
        ) {
          this.openSendOverrideDialog(id, body, only.message, 'allow_in_unexpected_state');
          return;
        }
        if (only.code === 'invoice_already_sent' && !body.allow_resend) {
          this.openSendOverrideDialog(id, body, only.message, 'allow_resend');
          return;
        }
        if (
          only.code === 'stale_einvoice_address' &&
          !body.allow_stale_einvoice_address
        ) {
          this.openSendOverrideDialog(id, body, only.message, 'allow_stale_einvoice_address');
          return;
        }
        // einvoice_address_unverifiable is fail-closed and NOT
        // overridable: Apix could not verify the routing (it is down), so
        // overriding would only push toward a send that fails. Surface it
        // as a plain "try again later" message with no dialog and no
        // resend, distinct from the stale-address override flow above.
        if (only.code === 'einvoice_address_unverifiable') {
          this.snackBar.open(
            'Apix is currently unavailable, so the eInvoice routing could not be verified. Please try again later.',
            'Dismiss',
            { duration: 8000 },
          );
          return;
        }
      }
      const lines = violations.map(
        (v: InvoiceNotReadyViolation) => `• ${v.message}`,
      );
      this.snackBar.open(
        `Cannot send invoice:\n${lines.join('\n')}`,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Could not send invoice. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // openSendOverrideDialog asks the operator to confirm a send that one
  // overridable precondition blocked — an unexpected invoice state, an
  // already-sent invoice, or a stale eInvoice address. On confirm,
  // re-POSTs with the matching override flag set; on cancel, no-ops. The
  // retry routes back through handleSendInvoiceError so a different failure
  // on the retry still surfaces. einvoice_address_unverifiable is NOT
  // handled here: it is fail-closed and non-overridable.
  //
  // The re-send copy states the outcome honestly, exactly as the
  // subfinancing twin does: Apix's duplicate detection keys on the seller's
  // business id plus the invoice number, so a re-send under the same number
  // is refused and recorded as failed rather than delivered.
  private openSendOverrideDialog(
    id: number,
    body: SendInvoiceRequestBody,
    message: string,
    override: 'allow_in_unexpected_state' | 'allow_resend' | 'allow_stale_einvoice_address',
  ): void {
    const dialogData = new ConfirmationDialogModel(
      this.sendOverrideDialogTitle(override),
      override === 'allow_resend'
        ? `${message}\n\nApix rejects a re-send under the same invoice number as a duplicate, so nothing new will be delivered and the attempt is recorded as failed. If the document has been corrected, the invoice number has to be bumped before it can reach the end customer.`
        : message,
      this.sendOverrideConfirmLabel(override),
      'Cancel',
    );
    this.dialog
      .open(ConfirmationDialogComponent, { maxWidth: '480px', data: dialogData })
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe(() => {
        this.postSendInvoice(id, { ...body, [override]: true });
      });
  }

  // sendOverrideDialogTitle picks the confirmation title for the override
  // being consented to.
  private sendOverrideDialogTitle(
    override: 'allow_in_unexpected_state' | 'allow_resend' | 'allow_stale_einvoice_address',
  ): string {
    switch (override) {
      case 'allow_resend':
        return 'Re-send invoice?';
      case 'allow_stale_einvoice_address':
        return 'Send to outdated eInvoice address?';
      default:
        return 'Send invoice anyway?';
    }
  }

  // sendOverrideConfirmLabel picks the confirm-button label for the
  // override being consented to.
  private sendOverrideConfirmLabel(
    override: 'allow_in_unexpected_state' | 'allow_resend' | 'allow_stale_einvoice_address',
  ): string {
    return override === 'allow_resend' ? 'Re-send' : 'Send anyway';
  }

  // Send to Subfinancing routes through
  // /api/invoices/{id}/send-subfinancing: the invoice's subfinancing copy is
  // regenerated and transmitted to the provider configured on its
  // InvoiceConfiguration. This is the automated form of the manual "Generate
  // Subfinancing Invoice → download → deliver by hand" flow, which stays in
  // the menu as the backup.
  //
  // Confirmed upfront for the same reason the end-customer send is: once the
  // package reaches Apix it cannot be recalled. A subsequent 422-recovery
  // retry re-enters via postSendSubfinancingInvoice and is NOT re-prompted.
  public sendToSubfinancing(): void {
    // A hold refuses the send whatever the overrides; the menu entry is
    // disabled for it, and the server refuses whatever this misses.
    if (!this.invoice?.subfinancing || this.invoice.financing_on_hold) {
      return;
    }
    const invoice = this.invoice;
    this.confirmationService
      .confirm(this.buildSendSubfinancingConfirmation(invoice))
      .pipe(filter((confirmed) => confirmed))
      .subscribe(() => this.postSendSubfinancingInvoice(invoice.id, {}));
  }

  // The Send to Subfinancing entry's tooltip while a financing hold applies:
  // the holds' scopes and reasons. Empty otherwise.
  public subfinancingHoldTooltip(): string {
    return this.invoice?.financing_on_hold ? financingHoldsTooltip(this.invoice.financing_holds) : '';
  }

  // buildSendSubfinancingConfirmation names the provider the package is going
  // to, so the operator confirms against the actual recipient rather than a
  // generic "subfinancing". The end customer is shown too because they remain
  // the invoice's buyer — only the transmission envelope changes.
  //
  // Amount is `invoice.amount` (the total billed to the end customer), the
  // figure the transmitted document carries — NOT `amount_financed`, our
  // advance to the client.
  private buildSendSubfinancingConfirmation(invoice: Invoice): ConfirmationDialogModel {
    const provider = invoice.subfinancing?.provider_name ?? 'the subfinancing provider';
    const details: ConfirmationDialogDetail[] = [
      { label: 'Provider:', value: provider },
      { label: 'End customer:', value: invoice.end_customer?.name || this.placeholder },
      { label: 'Amount:', value: this.formatMoney(invoice.amount) },
      { label: 'Due date:', value: invoice.due_date || this.placeholder },
    ];
    return new ConfirmationDialogModel(
      `Send this invoice to ${provider}?`,
      `This transmits the subfinancing invoice to ${provider} via Apix. Once sent it cannot be recalled. The invoice state is not changed.`,
      'Send to subfinancing',
      'Cancel',
      details,
    );
  }

  // postSendSubfinancingInvoice wraps the POST so the 422 → confirm-and-retry
  // path can re-POST the same body shape from one place, mirroring
  // postSendInvoice.
  private postSendSubfinancingInvoice(
    id: number,
    body: SendSubfinancingInvoiceRequestBody,
  ): void {
    this.invoicesService.sendSubfinancingInvoice({ id, body }).subscribe({
      next: () => this.notifySubfinancingSendStarted(),
      error: (err: HttpErrorResponse) => this.handleSendSubfinancingError(err, id, body),
    });
  }

  private notifySubfinancingSendStarted(): void {
    this.snackBar.open(
      'Subfinancing send started. The sent package will appear in Attachments once transmitted.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // handleSendSubfinancingError mirrors handleSendInvoiceError over this
  // endpoint's two overridable violations. The configuration violations
  // (missing_subfinancing_routing, missing_subfinancing_invoice_number_suffix)
  // are deliberately NOT overridable — they are defects to fix — so they fall
  // through to the multi-line snackbar like any other violation.
  //
  // Unlike the end-customer send, a 422 here routinely carries BOTH
  // overridable violations at once: re-sending after the invoice has been
  // financed is outside WaitingForFinancing *and* a repeat. Bailing to a
  // read-only snackbar on `violations.length !== 1` would dead-end the
  // operator on a combination the endpoint accepts, so the confirmations
  // chain and the retry carries both flags.
  private handleSendSubfinancingError(
    err: HttpErrorResponse,
    id: number,
    body: SendSubfinancingInvoiceRequestBody,
  ): void {
    if (err.status === 422 && this.isInvoiceNotReadyForSendError(err.error)) {
      const violations = err.error.violations;
      const overrides = this.pendingSubfinancingOverrides(violations, body);
      // Every violation must be overridable and not already overridden;
      // otherwise the retry would fail on whatever is left.
      if (overrides.length > 0 && overrides.length === violations.length) {
        this.confirmSubfinancingOverrides(id, overrides, body);
        return;
      }
      const lines = violations.map(
        (v: InvoiceNotReadyViolation) => `• ${v.message}`,
      );
      this.snackBar.open(
        `Cannot send to subfinancing:\n${lines.join('\n')}`,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Could not send to subfinancing. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // pendingSubfinancingOverrides maps the 422's violations onto the override
  // flags that would clear them, in confirmation order (state first, then the
  // re-send — the order they occur in the invoice's life). A violation whose
  // flag the request already carries is left out, so a retry can never loop:
  // the caller then finds fewer overrides than violations and falls through
  // to the snackbar.
  private pendingSubfinancingOverrides(
    violations: InvoiceNotReadyViolation[],
    body: SendSubfinancingInvoiceRequestBody,
  ): SubfinancingOverride[] {
    const codes = new Set(violations.map((v) => v.code));
    const messageFor = (code: string): string =>
      violations.find((v) => v.code === code)?.message ?? '';

    const pending: SubfinancingOverride[] = [];
    if (codes.has('invoice_state_not_waiting_for_financing') && !body.allow_in_unexpected_state) {
      pending.push({
        flag: 'allow_in_unexpected_state',
        message: messageFor('invoice_state_not_waiting_for_financing'),
      });
    }
    if (codes.has('invoice_already_sent_to_subfinancing') && !body.allow_resend) {
      pending.push({
        flag: 'allow_resend',
        message: messageFor('invoice_already_sent_to_subfinancing'),
      });
    }
    return pending;
  }

  // confirmSubfinancingOverrides walks the pending overrides one dialog at a
  // time, accumulating the confirmed flags, and re-POSTs once when the last
  // one is confirmed. Declining any dialog aborts the whole retry — a partial
  // re-POST would just fail on the override the operator refused. The retry
  // routes back through handleSendSubfinancingError so a different failure on
  // it still surfaces.
  private confirmSubfinancingOverrides(
    id: number,
    pending: SubfinancingOverride[],
    accepted: SendSubfinancingInvoiceRequestBody,
  ): void {
    const [next, ...rest] = pending;
    if (!next) {
      this.postSendSubfinancingInvoice(id, accepted);
      return;
    }
    this.dialog
      .open(ConfirmationDialogComponent, {
        maxWidth: '480px',
        data: this.subfinancingOverrideDialogModel(next),
      })
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe(() => {
        this.confirmSubfinancingOverrides(id, rest, { ...accepted, [next.flag]: true });
      });
  }

  // subfinancingOverrideDialogModel writes the confirmation for one override.
  //
  // The re-send copy states the outcome honestly: Apix's duplicate detection
  // keys on the seller's business id plus the invoice number, and the
  // subfinancing invoice number is deterministic — so re-sending the same
  // invoice reproduces the key and Apix refuses it. The send is then recorded
  // as failed, not delivered. Delivering a *corrected* document needs a fresh
  // invoice number first, which is an ops-level change today.
  private subfinancingOverrideDialogModel(
    override: SubfinancingOverride,
  ): ConfirmationDialogModel {
    if (override.flag === 'allow_resend') {
      return new ConfirmationDialogModel(
        'Re-send to subfinancing?',
        `${override.message}\n\nApix rejects a re-send under the same invoice number as a duplicate, so nothing new will be delivered and the attempt is recorded as failed. If the document has been corrected, the invoice number has to be bumped before it can reach the provider.`,
        'Re-send',
        'Cancel',
      );
    }
    return new ConfirmationDialogModel(
      'Send to subfinancing anyway?',
      override.message,
      'Send anyway',
      'Cancel',
    );
  }

  private isInvoiceNotReadyForSendError(body: unknown): body is InvoiceNotReadyError {
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const candidate = body as Partial<InvoiceNotReadyError>;
    return (
      candidate.error_code === 'invoice_not_ready_for_send' &&
      Array.isArray(candidate.violations)
    );
  }

  public sendModifiedOriginalInvoice(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicePeerService.sendModifiedOriginalInvoice(this.invoice.id);
  }

  public sendToAccounting(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicesService.sendInvoiceToAccounting({ id: this.invoice.id }).subscribe({
      error: (err: HttpErrorResponse) => this.handleSendToAccountingError(err),
    });
  }

  // Surfaces Netvisor's verbatim rejection text when the backend classified
  // the failure as user-fixable (HTTP 400 / FAILED) and falls back to a
  // generic snackbar for any other failure so the click is never silent.
  private handleSendToAccountingError(err: HttpErrorResponse): void {
    if (err.status === 422 && this.isNetvisorRejected(err.error)) {
      this.snackBar.open(
        err.error.message,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Failed to send to accounting. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  private isNetvisorRejected(body: unknown): body is { error_code: 'netvisor_rejected'; message: string } {
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const candidate = body as { error_code?: unknown; message?: unknown };
    return candidate.error_code === 'netvisor_rejected' && typeof candidate.message === 'string';
  }

  // Pre-fills the email recipients sub-form from the client representative
  // attached to the modern Invoice. Mirrors the v1 widget's pre-fill, but
  // sourced from `invoice.client_representative_id` instead of a
  // LegacyInvoice round-trip — the only behavioural delta versus v1.
  private getClientRepresentativeEmail(invoice: Invoice): Observable<string | null> {
    const id = invoice.client_representative_id;
    if (id === null || id === undefined) {
      return of(null);
    }
    return this.clientRepresentativeResource.get(id).pipe(
      map((rep: ClientRepresentative) => rep.email ?? null),
    );
  }

  // handleGenerateOfferError routes a 422 by violation set:
  //   1. State-only violation (just invoice_state_not_waiting_for_offer):
  //      open a confirmation dialog; on confirm, re-POST with
  //      allow_in_unexpected_state=true.
  //   2. Any other violation set (including mixed with the state one):
  //      surface every violation in the multi-line snackbar. The
  //      operator must fix the other readiness issues first; once
  //      those are resolved, the state-only path becomes available.
  // Non-422 errors fall through to a generic snackbar so the click is
  // never silent.
  private handleGenerateOfferError(
    err: HttpErrorResponse,
    id?: number,
    body?: GenerateOfferRequestBody,
  ): void {
    if (err.status === 422 && this.isInvoiceNotReadyError(err.error)) {
      const violations = err.error.violations;
      const onlyStateViolation =
        violations.length === 1 &&
        violations[0].code === 'invoice_state_not_waiting_for_offer';
      if (onlyStateViolation && id !== undefined && body !== undefined && !body.allow_in_unexpected_state) {
        this.openAllowInUnexpectedStateDialog(id, body, violations[0].message);
        return;
      }
      const lines = violations.map(
        (v: InvoiceNotReadyViolation) => `• ${v.message}`,
      );
      this.snackBar.open(
        `Cannot generate offer:\n${lines.join('\n')}`,
        'Dismiss',
        { duration: 10000, panelClass: 'multiline-snackbar' },
      );
      return;
    }
    this.snackBar.open(
      'Could not generate offer. Please try again.',
      'Dismiss',
      { duration: 5000 },
    );
  }

  // openAllowInUnexpectedStateDialog asks the operator whether to
  // proceed despite the invoice being in a state other than
  // WaitingForOfferToBeSent. On confirm, re-POSTs with the override
  // flag set; on cancel, no-ops. The retry's response routes back
  // through handleGenerateOfferError so a non-state failure on the
  // retry still surfaces.
  private openAllowInUnexpectedStateDialog(
    id: number,
    body: GenerateOfferRequestBody,
    message: string,
  ): void {
    const dialogData = new ConfirmationDialogModel(
      'Send offer anyway?',
      message,
      'Send anyway',
      'Cancel',
    );
    this.dialog
      .open(ConfirmationDialogComponent, { maxWidth: '480px', data: dialogData })
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe(() => {
        this.postGenerateOffer(id, { ...body, allow_in_unexpected_state: true });
      });
  }

  private isInvoiceNotReadyError(body: unknown): body is InvoiceNotReadyError {
    if (typeof body !== 'object' || body === null) {
      return false;
    }
    const candidate = body as Partial<InvoiceNotReadyError>;
    return (
      candidate.error_code === 'invoice_not_ready_for_offer' &&
      Array.isArray(candidate.violations)
    );
  }

  // The legacy auto-complete components emit `LeadSource`/`Partner`
  // legacy models with PascalCase fields. The modern Invoice's
  // `lead_source` / `responsible_seller` use the cool-api shape with
  // numeric `id`/`name`. The widget needs the legacy shape to seed
  // the auto-complete's `[current]` input, so we project here.
  private toLegacyLeadSource(modern: { id?: number; name?: string }): LeadSource {
    return { leadSourceId: modern.id ?? 0, name: modern.name ?? '' } as LeadSource;
  }

  private toLegacyPartner(modern: { id?: number; name?: string }): Partner {
    return { partnerId: modern.id ?? 0, name: modern.name ?? '' } as Partner;
  }

  private buildPutBody(invoice: Invoice): ReplaceInvoiceInformationRequest {
    const c = this.form.controls;

    const ibanNormalised = toElectronicIban(c['iban'].value);

    // `date`, `due_date`, `number` and `amount` can be unknown early on, and
    // the server keeps the stored value of each one the body leaves out: an
    // invoice's Information is corrected, never cleared. So an empty field is
    // left out, never sent as a placeholder ('' or 0) that would overwrite
    // an unknown detail, and a field with a value, typed or stored, is sent.
    // `date` is also left out when its day equals the stored one: a
    // cctool-go rollback then breaks only the saves that change the date,
    // and the server checks the date rules only on a change anyway.
    const date = toIsoDate(c['date'].value);
    const dueDate = toIsoDate(c['due_date'].value);
    const amount = this.toStringOrNull(c['amount'].value);
    const number = this.toStringOrNull(c['number'].value);
    // The original reference number is corrected, never cleared, like the
    // details above, but sent only when the handler changed it: a stored
    // reference that intake left invalid must not block an unrelated save,
    // and the server normalises and checks every value it is sent. An
    // emptied field is left out, so the stored reference is kept.
    const originalReference = this.toStringOrNull(
      String(c['original_reference_number'].value ?? '').trim(),
    );
    const originalReferenceChanged =
      originalReference !== null && originalReference !== (invoice.original_reference_number ?? null);

    return {
      ...(amount !== null ? { amount: { amount, currency: invoice.amount?.currency ?? 'EUR' } } : {}),
      iban: ibanNormalised,
      ...(date !== null && date !== invoice.date ? { date } : {}),
      ...(dueDate !== null ? { due_date: dueDate } : {}),
      express_payment: Boolean(c['express_payment'].value ?? invoice.express_payment ?? false),
      new_invoice_number: this.toIntOrNull(c['new_invoice_number'].value),
      new_reference_number: this.toStringOrNull(c['new_reference_number'].value),
      ...(number !== null ? { number } : {}),
      ...(originalReferenceChanged ? { original_reference_number: originalReference } : {}),
      lead_source_id: this.toIntOrNull(c['lead_source_id'].value),
      responsible_seller_id: this.toIntOrNull(c['responsible_seller_id'].value),
    };
  }

  // Refuses an IBAN the handler changed whose structure or check digits
  // ibantools rejects, in the electronic format the request sends. The IBAN
  // the form opened with passes untouched, valid or not, so a stored value
  // never blocks an unrelated save in the browser; the server still decides
  // on it. An empty field passes too.
  private validateChangedIban(control: AbstractControl): ValidationErrors | null {
    const iban = toElectronicIban(control.value);
    if (iban === null || iban === this.editIban || ibantools.isValidIBAN(iban)) {
      return null;
    }
    return { iban: true };
  }

  private toIntOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private toStringOrNull(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return String(value);
  }

  private toIsoDateOrFallback(value: unknown, fallback: string | undefined): string {
    return toIsoDate(value) ?? fallback ?? '';
  }

  private handleError(err: HttpErrorResponse): void {
    if (err.status === 412) {
      this.reloadMessage =
        'Invoice was updated by another user. Reload to see the latest.';
      return;
    }
    if (err.status === 422 && err.error?.error_code === 'invoice_state_locked') {
      const stateName = err.error?.details?.state ?? 'current state';
      this.stateLockedMessage = `Editing is not allowed in state "${stateName}".`;
      return;
    }
    // The widget sends a stored IBAN back without its dashes, which the
    // server's normaliser keeps, so in WaitingForReport or Completed the
    // server can refuse a stored IBAN that the widget found valid. Bank
    // Account is read-only there, so this goes into the banner.
    if (err.status === 422 && err.error?.error_code === 'invoice_persisted_iban_invalid') {
      this.bannerFieldErrors = [`${FIELD_LABELS.iban}: ${PERSISTED_IBAN_INVALID}`];
      return;
    }
    // Both carry per-field errors. The date checks can report two rules on
    // `date` at once, so one field's messages are joined rather than the last
    // one winning. Each field's messages show under the field when it is on
    // screen, and otherwise in the error banner under the field's label. So a
    // refused save always says why. An invalid `iban` off screen, in
    // WaitingForReport or Completed where Bank Account is read-only, is the
    // stored IBAN the widget sent back, which the handler cannot change here:
    // the banner says whom to ask, as for invoice_persisted_iban_invalid. A
    // locked `iban` there means someone changed a valid IBAN after the form
    // opened, so it keeps the server's words and the handler reloads.
    if (
      err.status === 422 &&
      (err.error?.error_code === 'invoice_field_locked' ||
        err.error?.error_code === 'invoice_information_invalid')
    ) {
      const ibanInvalid = err.error?.error_code === 'invoice_information_invalid';
      const rawErrors: ReadonlyArray<FieldError> = err.error?.errors ?? [];
      const byField: Record<string, string> = {};
      for (const e of rawErrors) {
        byField[e.field] = byField[e.field] ? `${byField[e.field]}; ${e.message}` : e.message;
      }
      const policy = this.editablePolicy(this.invoice);
      const inline: Record<string, string> = {};
      const banner: string[] = [];
      for (const [field, message] of Object.entries(byField)) {
        if (isInformationField(field) && policy[field]) {
          inline[field] = message;
        } else if (field === 'iban' && ibanInvalid) {
          banner.push(`${FIELD_LABELS.iban}: ${PERSISTED_IBAN_INVALID}`);
        } else {
          banner.push(`${isInformationField(field) ? FIELD_LABELS[field] : field}: ${message}`);
        }
      }
      this.fieldErrors = inline;
      this.bannerFieldErrors = banner;
      return;
    }
    // The HttpErrorInterceptor opens no dialog for a body with an error_code
    // and a message, and leaves it to the widget. So a refusal the widget
    // does not know goes into the banner in the server's words, or as
    // "Saving failed" when those are blank. Any other failed save, such as
    // an uncoded 422 or a bare 400, already got the interceptor's dialog,
    // so the banner does not repeat it.
    if (this.isCodedError(err.error)) {
      this.bannerFieldErrors = [err.error.message.trim() || SAVING_FAILED];
    }
  }

  private clearErrors(): void {
    this.stateLockedMessage = null;
    this.reloadMessage = null;
    this.fieldErrors = {};
    this.bannerFieldErrors = [];
  }
}
